import { getAlertsSummaryDataWithPagination, getAlertsSummaryData } from '@src/api/aiAlerts';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { aiAlertsDimensionMappingSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { formatAIAlertSummary } from './utils';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';

export const useFetchAlerts = (interval, panelEnabled = false) => {
  const { from, to } = interval;
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);

  const {
    data: alerts,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    ['ai-alerts-list', interval],
    () => {
      if (enableDPIAlertPagination) {
        const requestBody = {
          from: moment(from).format(),
          to: moment(to).format(),
          limit: 200, // Get latest 200 alerts
          cursor: null, // Start from the beginning
          cache: true,
          filters: {},
          search: {},
          sortBy: 'eventTime', // Sort by event time to get latest alerts
          sortOrder: 'DESC', // Descending order for latest first
          includeCounts: false, // Don't need counts for overview panel
          getAll: false, // Use pagination, don't get all
        };

        return getAlertsSummaryDataWithPagination(requestBody).then((response) => {
          const responseData = get(response, ['data'], {});
          // Alerts are in responseData.data (following the same pattern as useFetchAlertsWithPagination)
          const alerts = get(responseData, ['data'], []);
          return alerts;
        });
      }

      return getAlertsSummaryData({
        params: {
          from: moment(from).format(),
          to: moment(to).format(),
          cache: true,
        },
      }).then((response) => {
        const data = get(response, ['data'], {});
        return data;
      });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false, // Prevent refetch when panel is shown
      retry: false,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 10 * 60 * 1000, // 10m
      enabled: !!interval && !!to && !!from && panelEnabled,
    },
  );

  const result = alerts?.length
    ? formatAIAlertSummary(
        alerts,
        dimensionListObject as any,
        dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
      )
    : [];

  return {
    alerts: result,
    isEmpty: result?.length === 0,
    isFetching,
    isError,
    refetch,
  };
};
